/** @format */

import { FileService, UtilityService } from '@shared/data-access/services';
import { Injectable, inject } from '@angular/core';
import { Observable, of, zip } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { selectDescriptiveAttributes, selectPrimaryCategory } from '@pdp/data-access/state';

import { Environment } from '@shared/data-access/interfaces';
import { Store } from '@ngrx/store';

/**
 * @description
 * Service responsible for determining if Q&A widget should be visible
 */
@Injectable({
	providedIn: 'root'
})
export class QAWidgetService {
	private readonly environment = inject(Environment);
	private readonly fileService = inject(FileService);
	private readonly utilityService = inject(UtilityService);
	private readonly store = inject(Store);

	/**
	 * @description
	 * Determines if the Q&A widget should be visible
	 * @param chainMarketingIdentifierAbbr The chain marketing identifier abbreviation
	 * @returns Observable<boolean> True if the widget should be visible
	 */
	shouldShowQAWidget(chainMarketingIdentifierAbbr: string): Observable<boolean> {
		const isQnAEnabled =
			this.environment.feature.questionAndAnswer[chainMarketingIdentifierAbbr]?.enabled;

		if (chainMarketingIdentifierAbbr !== 'dsg' || !isQnAEnabled) {
			return of(false);
		}

		return zip(
			this.store.select(selectDescriptiveAttributes),
			this.store.select(selectPrimaryCategory)
		).pipe(
			take(1),
			map(([descriptiveAttributes, primaryCategoryData]) => {
				if (!descriptiveAttributes || !primaryCategoryData) {
					return false;
				}

				// Check if product has QA attribute set to Yes
				const hasQAAttribute = descriptiveAttributes.some(
					(attr) => attr.identifier === '5733' && attr.value === 'Yes'
				);

				if (hasQAAttribute) {
					return true;
				}

				const primaryCategoryName =
					typeof primaryCategoryData.note === 'string' &&
					primaryCategoryData.note.trim() !== ''
						? primaryCategoryData.note
						: primaryCategoryData.name;

				const manufacturer = descriptiveAttributes.find(
					(attr) => attr.identifier === 'X_BRAND'
				);

				if (!manufacturer?.value) {
					return false;
				}

				// Return an observable chain that checks both category and brand lists
				return zip(
					this.isItemInCategoryList$(
						primaryCategoryName ?? '',
						'QACategories',
						chainMarketingIdentifierAbbr
					),
					this.isItemInCategoryList$(
						manufacturer.value.toString(),
						'QABrands',
						chainMarketingIdentifierAbbr
					)
				).pipe(
					map(
						([categoryInQACategoryList, categoryInQABrandList]) =>
							categoryInQACategoryList || categoryInQABrandList
					)
				);
			}),
			switchMap((result) => {
				return typeof result === 'boolean' ? of(result) : result;
			})
		);
	}

	/**
	 * @description
	 * Checks if an item is in a specific category list
	 * @param item The item to check
	 * @param categoryName The name of the category to check against
	 * @param storeName The store name to check in
	 * @returns Observable<boolean> True if the item is in the list
	 */
	private isItemInCategoryList$(
		item: string,
		categoryName: string,
		storeName?: string
	): Observable<boolean> {
		return this.fileService.getDataFileByType$('categoriesData', 'json').pipe(
			map(
				(
					data: Record<string, string[]> & {
						dsg: Record<string, string[]>;
						gg: Record<string, string[]>;
					}
				) => {
					let categoryList: string[];

					if (storeName) {
						categoryList = data[storeName] ? data[storeName][categoryName] : null;
					} else {
						categoryList = data[categoryName];
					}

					return this.utilityService.isItemInList(item, categoryList);
				}
			)
		);
	}
}
